import React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";

const isReactElement = (node: unknown): node is React.ReactElement =>
  React.isValidElement(node);

const PopoverTooltip = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    disablePortal?: boolean;
  }
>(
  (
    {
      className,
      align = "center",
      sideOffset = 4,
      disablePortal,
      children, // Trigger
      content, // Content
      ...props
    },
    ref,
  ) => {
    const popoverContent = (
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          "text-base text-foreground font-normal",
          "select-none will-change-[transform,opacity]",
          "z-50",
          className,
        )}
        {...props}
      >
        {content}
      </PopoverPrimitive.Content>
    );

    return (
      <PopoverPrimitive.Root>
        <PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>
        {disablePortal ? (
          popoverContent
        ) : (
          <PopoverPrimitive.Portal>{popoverContent}</PopoverPrimitive.Portal>
        )}
      </PopoverPrimitive.Root>
    );
  },
);
PopoverTooltip.displayName = PopoverPrimitive.Popover.displayName;

const Tooltip = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.TooltipTrigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.TooltipTrigger>
>(({ children, className, content, ...props }, ref) => {
  // Check if the child is a React element and is disabled
  const shouldWrap =
    isReactElement(children) &&
    "props" in children &&
    "disabled" in children.props &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    Boolean(children.props.disabled);

  const wrappedChild = shouldWrap ? (
    <span className="inline-block" tabIndex={-1}>
      {children}
    </span>
  ) : (
    children
  );

  return (
    <TooltipPrimitive.TooltipProvider delayDuration={0} {...props}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger ref={ref} asChild {...props}>
          {wrappedChild}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            className={cn(
              "px-[15px] py-2.5 bg-[white] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] rounded",
              "text-base leading-none",
              "select-none will-change-[transform,opacity]",
              "z-50",
              className,
            )}
          >
            {content}
            <TooltipPrimitive.TooltipArrow className="fill-white" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.TooltipProvider>
  );
});
Tooltip.displayName = TooltipPrimitive.TooltipProvider.displayName;

export { Tooltip, PopoverTooltip };
